<template>
  <div class="message-input">
    <!-- File Upload Preview -->
    <div v-if="uploadingFiles.length > 0 || selectedFiles.length > 0" class="file-upload-preview">
      <div class="preview-header">
        <h4>Files to send:</h4>
        <button @click="clearSelectedFiles" class="clear-files-btn">
          Clear All
        </button>
      </div>

      <!-- Selected Files Preview -->
      <div class="file-preview-grid">
        <div v-for="(file, index) in selectedFiles" :key="index" class="file-preview-item">
          <div class="file-preview-content">
            <div v-if="file.category === 'image'" class="image-preview">
              <img :src="file.previewUrl" :alt="file.name" />
            </div>
            <div v-else class="file-icon">
              {{ getFileIcon(file.extension) }}
            </div>
            <div class="file-info">
              <span class="file-name">{{ file.name }}</span>
              <span class="file-size">{{ formatFileSize(file.size) }}</span>
            </div>
          </div>
          <button @click="removeSelectedFile(index)" class="remove-file-btn">
            ×
          </button>
        </div>
      </div>

      <!-- Uploading Files Progress -->
      <div v-for="(upload, index) in uploadingFiles" :key="'upload-' + index" class="uploading-file">
        <div class="upload-info">
          <span class="file-name">{{ upload.file.name }}</span>
          <span class="upload-progress">{{ upload.progress }}%</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: upload.progress + '%' }"></div>
        </div>
      </div>

      <!-- Send Files Button -->
      <button v-if="selectedFiles.length > 0 && uploadingFiles.length === 0" @click="sendFiles" class="send-files-btn">
        Send {{ selectedFiles.length }} file{{
          selectedFiles.length > 1 ? "s" : ""
        }}
      </button>
    </div>

    <div class="form__item">
      <b-form-textarea class="form__input" id="textarea-default" rows="1" max-rows="4"
        placeholder="Type a message…" v-model="messageText"
        @keydown.enter="handleEnterKey" :disabled="disabled || uploadingFiles.length > 0">
      </b-form-textarea>

      <div class="emoji-picker-wrapper">
        <button class="emoji-invoker" @click="showEmojiPicker = !showEmojiPicker" type="button" :disabled="disabled">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 fill-current text-grey">
            <path d="M0 0h24v24H0z" fill="none" />
            <path
              d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
          </svg>
        </button>
        <EmojiPicker v-if="showEmojiPicker" :native="true" :hide-search="false" :hide-group-icons="false"
          :hide-group-names="false" @select="onSelectEmoji" class="emoji-picker-popup" ref="emojiPicker" />
      </div>

      <div class="send-imag-buttons-wrapper">
        <!-- File Upload Button -->
        <button type="button" class="file-button" title="Attach file" :disabled="disabled" @click="openFilePicker">
          <i class="bi bi-paperclip"></i>
        </button>

        <!-- File Input - positioned outside for better browser compatibility -->
        <input ref="fileInput" type="file" @change="handleFileSelect" multiple
          accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.avi,.mov,.wmv,.flv,.webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.zip,.rar,.7z,.tar,.gz,.mp3,.wav,.ogg,.flac,.m4a"
          class="sr-only-file-input" :disabled="disabled" />

        <!-- Create Poll Button -->
        <button type="button" class="file-button poll-button" title="Create poll" :disabled="disabled"
          @click="openPollModal">
          <i class="bi bi-bar-chart-fill"></i>
        </button>

        <b-button id="save_chat_message" @click="handleSendMessage" variant="success" :disabled="disabled ||
          (!messageText.trim() && selectedFiles.length === 0) ||
          uploadingFiles.length > 0
          ">
          <i class="bi bi-arrow-right-circle" aria-hidden="true"></i>
        </b-button>
      </div>
    </div>

    <!-- Create Poll modal -->
    <b-modal v-model="showPollModal" title="Create a poll" hide-footer centered>
      <div class="poll-create">
        <label class="poll-label">Question</label>
        <input v-model="pollQuestion" class="poll-input" maxlength="200"
          placeholder="Ask a question…" />

        <label class="poll-label">Options</label>
        <div v-for="(opt, i) in pollOptions" :key="i" class="poll-opt-row">
          <input v-model="pollOptions[i]" class="poll-input" maxlength="100"
            :placeholder="'Option ' + (i + 1)" />
          <button v-if="pollOptions.length > 2" type="button" class="poll-opt-remove"
            title="Remove option" @click="pollOptions.splice(i, 1)">×</button>
        </div>
        <button v-if="pollOptions.length < 12" type="button" class="poll-add-opt"
          @click="pollOptions.push('')">
          <i class="bi bi-plus-lg"></i> Add option
        </button>

        <label class="poll-multi">
          <input type="checkbox" v-model="pollAllowMultiple" />
          Allow multiple answers
        </label>

        <div class="poll-actions">
          <button type="button" class="poll-cancel" @click="showPollModal = false">Cancel</button>
          <button type="button" class="poll-send" :disabled="!canCreatePoll" @click="createPoll">
            Create poll
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import EmojiPicker from "vue3-emoji-picker";
import "vue3-emoji-picker/css";
import ChatService from "@/services/ChatService";

export default {
  name: "ChatInput",
  components: { EmojiPicker },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
      default: "",
    },
    clubId: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "send-message", "send-files", "send-poll"],
  data() {
    return {
      showEmojiPicker: false,
      messageText: this.modelValue,
      selectedFiles: [],
      uploadingFiles: [],
      showPollModal: false,
      pollQuestion: "",
      pollOptions: ["", ""],
      pollAllowMultiple: false,
    };
  },
  computed: {
    canCreatePoll() {
      return (
        this.pollQuestion.trim().length > 0 &&
        this.pollOptions.filter((o) => o.trim().length > 0).length >= 2
      );
    },
  },
  watch: {
    modelValue(newValue) {
      this.messageText = newValue;
    },
    messageText(newValue) {
      this.$emit("update:modelValue", newValue);
    },
  },
  methods: {
    handleSendMessage() {
      if (this.selectedFiles.length > 0) {
        this.sendFiles();
      } else if (this.messageText.trim() && !this.disabled) {
        this.$emit("send-message", this.messageText.trim());
        this.messageText = "";
        this.showEmojiPicker = false;
      }
    },
    handleEnterKey(event) {
      // Send on Enter; Shift+Enter falls through to the textarea default and
      // inserts a line break. Must be keydown — on keyup the newline has
      // already been inserted before we get to cancel it.
      if (!event.shiftKey) {
        event.preventDefault();
        this.handleSendMessage();
      }
    },
    onSelectEmoji(emoji) {
      if (this.messageText !== undefined) {
        this.messageText = this.messageText + emoji.i;
      } else {
        this.messageText = emoji.i;
      }
      this.showEmojiPicker = false;
    },

    // Poll Methods
    openPollModal() {
      this.pollQuestion = "";
      this.pollOptions = ["", ""];
      this.pollAllowMultiple = false;
      this.showPollModal = true;
    },

    createPoll() {
      const question = this.pollQuestion.trim();
      const options = this.pollOptions
        .map((o) => o.trim())
        .filter((o) => o.length > 0);
      if (!question || options.length < 2) return;
      this.$emit("send-poll", {
        question,
        options,
        allowMultiple: this.pollAllowMultiple,
      });
      this.showPollModal = false;
    },

    // File Upload Methods
    openFilePicker() {
      console.log("openFilePicker called");
      // Create a temporary file input to bypass any modal/overflow issues
      const tempInput = document.createElement('input');
      tempInput.type = 'file';
      tempInput.multiple = true;
      tempInput.accept = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar';

      tempInput.onchange = (event) => {
        console.log("Files selected:", event.target.files);
        const files = Array.from(event.target.files);
        this.processFiles(files);
      };

      // Trigger the file picker
      tempInput.click();
    },

    triggerFileUpload() {
      this.openFilePicker();
    },

    handleFileSelect(event) {
      console.log("handleFileSelect called", event.target.files);
      const files = Array.from(event.target.files);
      this.processFiles(files);
      // Clear the input so the same file can be selected again
      event.target.value = "";
    },

    processFiles(files) {
      files.forEach((file) => {
        if (this.validateFile(file)) {
          const fileObj = {
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
            extension: this.getFileExtension(file.name),
            category: this.getFileCategory(file.type),
            previewUrl: null,
          };

          // Create preview URL for images
          if (fileObj.category === "image") {
            fileObj.previewUrl = URL.createObjectURL(file);
          }

          this.selectedFiles.push(fileObj);
        }
      });
    },

    validateFile(file) {
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        this.$toast.error(
          `${file.name} is too large — the maximum size is 50MB.`,
        );
        return false;
      }

      // Validated by extension so the list stays in lockstep with the
      // backend allowlist (browser MIME types vary too much to rely on).
      const allowedExtensions = [
        "jpg", "jpeg", "png", "gif", "webp",
        "mp4", "avi", "mov", "wmv", "flv", "webm",
        "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "rtf",
        "zip", "rar", "7z", "tar", "gz",
        "mp3", "wav", "ogg", "flac", "m4a",
      ];

      const extension = this.getFileExtension(file.name);
      if (!allowedExtensions.includes(extension)) {
        this.$toast.error(
          `.${extension} files can't be shared here.`,
        );
        return false;
      }

      return true;
    },

    getFileExtension(filename) {
      return filename.split(".").pop().toLowerCase();
    },

    getFileCategory(type) {
      if (type.startsWith("image/")) return "image";
      if (type.startsWith("video/")) return "video";
      if (type.startsWith("audio/")) return "audio";
      return "document";
    },

    getFileIcon(extension) {
      const icons = {
        pdf: "📄",
        doc: "📄",
        docx: "📄",
        txt: "📄",
        xls: "📊",
        xlsx: "📊",
        ppt: "📊",
        pptx: "📊",
        zip: "📁",
        rar: "📁",
        "7z": "📁",
        mp3: "🎵",
        wav: "🎵",
        ogg: "🎵",
        flac: "🎵",
        mp4: "🎬",
        avi: "🎬",
        mov: "🎬",
        webm: "🎬",
      };
      return icons[extension] || "📄";
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    removeSelectedFile(index) {
      const file = this.selectedFiles[index];
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
      this.selectedFiles.splice(index, 1);
    },

    clearSelectedFiles() {
      this.selectedFiles.forEach((file) => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      this.selectedFiles = [];
    },

    async sendFiles() {
      if (this.selectedFiles.length === 0) return;

      for (const fileObj of this.selectedFiles) {
        const uploadProgress = {
          file: fileObj.file,
          progress: 0,
        };
        this.uploadingFiles.push(uploadProgress);

        try {
          const uploadResult = await ChatService.uploadFile(
            fileObj.file,
            (progress) => {
              uploadProgress.progress = progress;
            },
            this.clubId,
          );

          // Emit the file upload result along with any text message
          this.$emit("send-files", {
            files: [uploadResult.file],
            message: this.messageText || "",
          });

          // Remove from uploading
          const uploadIndex = this.uploadingFiles.indexOf(uploadProgress);
          if (uploadIndex > -1) {
            this.uploadingFiles.splice(uploadIndex, 1);
          }
        } catch (error) {
          console.error("Error uploading file:", error);
          this.$toast.error(
            `${fileObj.file.name} could not be uploaded. Please try again.`,
          );

          // Remove from uploading
          const uploadIndex = this.uploadingFiles.indexOf(uploadProgress);
          if (uploadIndex > -1) {
            this.uploadingFiles.splice(uploadIndex, 1);
          }
        }
      }

      // Clear selected files and message
      this.clearSelectedFiles();
      this.messageText = "";
    },
  },

  beforeUnmount() {
    // Clean up preview URLs
    this.clearSelectedFiles();
  },
};
</script>

<style scoped lang="scss">
/* Attach / poll / send as one compact row beside the textarea. The old
   fixed-width vertical stack (full-width buttons) towered to half the screen
   on phones and squeezed the textarea to a sliver. */
.send-imag-buttons-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
  flex-shrink: 0;

  button {
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
  }
}

.message-input {
  position: sticky;
  width: 100%;
  background: white;
  padding: 1rem;
  left: 0;
  bottom: 0;
  border-top: 1px solid #dee2e6;

  .file-upload-preview {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 15px;

    .preview-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;

      h4 {
        margin: 0;
        font-size: 16px;
        color: #333;
      }

      .clear-files-btn {
        background-color: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;

        &:hover {
          background-color: #c82333;
        }
      }
    }

    .file-preview-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 10px;
      margin-bottom: 15px;

      .file-preview-item {
        position: relative;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 10px;
        background-color: white;

        .file-preview-content {
          display: flex;
          align-items: center;
          gap: 10px;

          .image-preview {
            width: 40px;
            height: 40px;
            border-radius: 4px;
            overflow: hidden;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
          }

          .file-icon {
            font-size: 24px;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #e9ecef;
            border-radius: 4px;
          }

          .file-info {
            flex: 1;
            overflow: hidden;

            .file-name {
              display: block;
              font-weight: 500;
              color: #333;
              font-size: 14px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .file-size {
              display: block;
              color: #666;
              font-size: 12px;
            }
          }
        }

        .remove-file-btn {
          position: absolute;
          top: 5px;
          right: 5px;
          background-color: #dc3545;
          color: white;
          border: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;

          &:hover {
            background-color: #c82333;
          }
        }
      }
    }

    .uploading-file {
      margin-bottom: 10px;

      .upload-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 5px;

        .upload-progress {
          font-size: 12px;
          color: #666;
        }
      }

      .progress-bar {
        height: 4px;
        background-color: #e9ecef;
        border-radius: 2px;
        overflow: hidden;

        .progress-fill {
          height: 100%;
          background-color: #007bff;
          transition: width 0.3s ease;
        }
      }
    }

    .send-files-btn {
      background-color: #28a745;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 500;
      width: 100%;

      &:hover {
        background-color: #218838;
      }
    }
  }

  .form__item {
    position: relative;
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;

    .file-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background-color: #6c757d;
      color: white;
      border: none;
      padding: 8px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 16px;
      flex-shrink: 0;

      &:hover:not(:disabled) {
        background-color: #5a6268;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .sr-only-file-input {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    input.form__input,
    #textarea-default {
      flex: 1;
      min-width: 0;
      font-weight: normal !important;
      resize: vertical;
      min-height: 2.5rem;
      max-height: 120px;
    }

    // Phones: tighter gaps and a shorter default textarea so the row fits
    @media (max-width: 576px) {
      gap: 0.3rem;

      #textarea-default {
        padding: 0.4rem 0.6rem;
        font-size: 0.95rem;
      }
    }

    #save_chat_message {
      flex-shrink: 0;
      align-self: flex-end;
    }
  }
}

.emoji-picker-wrapper {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}

.emoji-invoker {
  position: relative;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
  background: transparent;
  border: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: scale(1.1);
    background: #f8f9fa;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 1.2rem;
    height: 1.2rem;
    fill: #6c757d;
  }
}

.emoji-picker-popup {
  position: fixed !important;
  z-index: 10000 !important;
  top: auto !important;
  bottom: 120px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  box-shadow: 1px 1px 8px #c7dbe6;
  border-radius: 0.5rem;
  max-width: 350px;
  max-height: 400px;
  overflow-y: auto;
  background: white;
}

/* For small screens, center it properly */
@media (max-width: 768px) {
  .emoji-picker-popup {
    max-width: 90vw !important;
    max-height: 60vh !important;
    left: 5vw !important;
    transform: none !important;
  }
}

/* Ensure it appears above modal content */
.modal-dialog .emoji-picker-popup {
  position: fixed !important;
  z-index: 10000 !important;
}

/* Poll button reuses the file-button chrome, tinted to stand apart. */
.poll-button {
  background-color: #0d6efd;

  &:hover:not(:disabled) {
    background-color: #0b5ed7;
  }
}

/* Create-poll modal body */
.poll-create {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .poll-label {
    font-weight: 600;
    font-size: 0.85rem;
    color: #333;
    margin: 0.5rem 0 0.1rem;
  }

  .poll-input {
    width: 100%;
    padding: 0.5rem 0.65rem;
    border: 1px solid #ced4da;
    border-radius: 6px;
    font-size: 0.95rem;

    &:focus {
      outline: none;
      border-color: #0d6efd;
      box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.15);
    }
  }

  .poll-opt-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;

    .poll-input {
      flex: 1;
    }

    .poll-opt-remove {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: none;
      background-color: #f1f3f5;
      color: #dc3545;
      font-size: 1.1rem;
      line-height: 1;
      cursor: pointer;

      &:hover {
        background-color: #f8d7da;
      }
    }
  }

  .poll-add-opt {
    align-self: flex-start;
    background: none;
    border: none;
    color: #0d6efd;
    font-size: 0.9rem;
    font-weight: 500;
    padding: 0.25rem 0;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .poll-multi {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.9rem;
    color: #333;
    margin-top: 0.5rem;
    cursor: pointer;

    input {
      width: auto;
      margin: 0;
    }
  }

  .poll-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1rem;

    button {
      padding: 0.5rem 1.1rem;
      border-radius: 6px;
      font-weight: 500;
      cursor: pointer;
      border: none;
    }

    .poll-cancel {
      background-color: #e9ecef;
      color: #333;

      &:hover {
        background-color: #dee2e6;
      }
    }

    .poll-send {
      background-color: #28a745;
      color: white;

      &:hover:not(:disabled) {
        background-color: #218838;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}
</style>
